import { useMutation, useQuery, useQueryClient, queryOptions, type QueryClient } from '@tanstack/react-query';
import { useAuthStoreV1 } from '@/stores/authStore_v1';
import { rulesApi } from '@/services/rules/api';
import type { RulesDocument, RulesDocuments, RulesScope } from '@/services/rules/types';

export const rulesKeys = {
  all: (orgId: string) => ['rules', orgId] as const,
  documents: (orgId: string) => ['rules', orgId, 'documents'] as const,
  versions: (orgId: string, scope: RulesScope) => ['rules', orgId, 'versions', scope] as const,
};

export const rulesDocumentsOptions = (orgId: string) =>
  queryOptions({
    queryKey: rulesKeys.documents(orgId),
    queryFn: () => rulesApi.get(orgId),
    // Fresh on every open; a stale cache must never feed `base_version`.
    staleTime: 0,
    refetchOnWindowFocus: false,
  });

export const rulesVersionsOptions = (orgId: string, scope: RulesScope, limit = 20) =>
  queryOptions({
    queryKey: rulesKeys.versions(orgId, scope),
    queryFn: () => rulesApi.versions(orgId, scope, limit),
  });

export function useRulesDocuments(options?: { enabled?: boolean }) {
  const orgId = useAuthStoreV1((s) => s.selectedOrg?.id ?? null);
  return useQuery({
    ...rulesDocumentsOptions(orgId ?? ''),
    enabled: !!orgId && (options?.enabled ?? true),
  });
}

export function useRulesVersions(scope: RulesScope, options?: { enabled?: boolean; limit?: number }) {
  const orgId = useAuthStoreV1((s) => s.selectedOrg?.id ?? null);
  return useQuery({
    ...rulesVersionsOptions(orgId ?? '', scope, options?.limit),
    enabled: !!orgId && (options?.enabled ?? true),
  });
}

export interface SaveRulesVariables {
  scope: RulesScope;
  content: string;
  baseVersion: number;
}

/**
 * Save mutation options, exported so the contract is testable directly.
 * Silent: the global handler would console.error the request error (rules text included)
 * and toast a second time; the save path reports once. No retry: a retried timed-out save
 * replays with a stale base_version and reads as a false 409.
 */
export function saveRulesMutationOptions(orgId: string | null, queryClient: QueryClient) {
  return {
    meta: { silent: true },
    retry: false as const,
    mutationFn: ({ scope, content, baseVersion }: SaveRulesVariables) => {
      if (!orgId) throw new Error('No organization selected');
      return rulesApi.save(orgId, scope, content, baseVersion);
    },
    onSuccess: (doc: RulesDocument, { scope }: SaveRulesVariables) => {
      if (!orgId) return;
      queryClient.setQueryData<RulesDocuments>(rulesKeys.documents(orgId), (prev) =>
        prev ? { ...prev, [scope]: doc } : prev,
      );
      void queryClient.invalidateQueries({ queryKey: rulesKeys.versions(orgId, scope) });
    },
  };
}

/** Save mutation. Errors surface raw; callers classify by status (`classifyRulesSaveError`). */
export function useSaveRules() {
  const orgId = useAuthStoreV1((s) => s.selectedOrg?.id ?? null);
  const queryClient = useQueryClient();
  return useMutation(saveRulesMutationOptions(orgId, queryClient));
}
